import { TimeInput24, toLocalInput, previewLabel, withDate, withTime } from "../DateTime24";
import { wallToCalendarDate } from "../../lib/tenantTime";
import { useState } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { SelectItem } from "../ui/select";
import { Route, CalendarIcon } from "lucide-react";
import { TRUCK_STATUS_COLORS } from "../../lib/constants";
import { cn, formatDistance, formatDuration } from "../../lib/utils";

export { toLocalInput };

const QUICK_OPTIONS = [
  { label: "Now", mins: 0 },
  { label: "+15m", mins: 15 },
  { label: "+30m", mins: 30 },
  { label: "+1h", mins: 60 },
  { label: "+2h", mins: 120 },
];

// ─────────────────────────────────────────────────────────────────────────────
// Scheduled-At picker: calendar popover + time input + quick "Now / +15m" chips.
// Pure render — value/onChange come from the parent so the parent owns state.
//
// `sameDayOnly` (Feb 2026 — AA/FR reassign UX): when true, hides the calendar
// popover so the dispatcher can't push the job to a future day. AA/FR jobs
// are urgent-turnaround by contract with the partner — reassigning them to a
// different truck is fine, but pushing them to tomorrow isn't. Only the
// quick-select chips remain (Now / +15m / +30m / +1h / +2h). Manual jobs
// still get the full picker.
// ─────────────────────────────────────────────────────────────────────────────
export function ScheduledAtPicker({ value, onChange, sameDayOnly = false }) {
  // Added Oct 2026: controlled `open` + Done button inside the popover
  // so this picker matches the DateTime24 UX everywhere else (same calendar,
  // same quick chips, same Done button). Previously the popover had no
  // explicit close trigger, so a dispatcher who only touched the time
  // input was stuck pressing Enter / clicking outside — the same
  // inconsistency OCD complaint that triggered this standardisation pass.
  const [open, setOpen] = useState(false);
  const bump = (mins) => onChange(toLocalInput(new Date(Date.now() + mins * 60 * 1000)));
  return (
    <div>
      <Label className="uppercase-label">Scheduled At</Label>
      {sameDayOnly ? (
        <div
          className="w-full flex items-center gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-3 py-2 text-sm"
          data-testid="assign-schedule-locked"
        >
          <CalendarIcon className="h-4 w-4 shrink-0 text-slate-400" />
          <span className="text-slate-700 dark:text-slate-300 font-normal">
            {previewLabel(value)}
          </span>
        </div>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start text-left font-normal text-base h-11"
              data-testid="assign-schedule-trigger"
            >
              <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
              {previewLabel(value)}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0"
            align="start"
            collisionPadding={16}
            data-testid="assign-schedule-popover"
          >
            <Calendar
              mode="single"
              selected={wallToCalendarDate(value)}
              onSelect={(d) => {
                if (!d) return;
                onChange(withDate(value, d));
              }}
              initialFocus
            />
            <div className="border-t border-slate-200 dark:border-slate-700 p-3 flex items-center gap-2">
              <Label className="uppercase-label !text-xs">Time (24h)</Label>
              <TimeInput24
                value={value ? value.slice(11, 16) : ""}
                onChange={(t) => {
                  if (!/^\d{2}:\d{2}$/.test(t)) return;
                  onChange(withTime(value, t));
                }}
                dataTestId="assign-schedule-time-input"
              />
            </div>
            <div className="border-t border-slate-200 dark:border-slate-700 p-3 flex flex-wrap gap-1.5">
              {QUICK_OPTIONS.map((q) => (
                <button
                  key={q.label}
                  type="button"
                  onClick={() => (q.mins === 0 ? onChange(toLocalInput(new Date())) : bump(q.mins))}
                  className="text-xs font-semibold px-2.5 py-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-800 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-blue-900/40 dark:hover:border-blue-700 dark:hover:text-blue-200 transition-colors"
                  data-testid={`assign-quick-${q.label.toLowerCase().replace('+', 'plus')}`}
                >
                  {q.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-md bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white transition-colors"
                data-testid="assign-schedule-done"
              >
                Done
              </button>
            </div>
          </PopoverContent>
        </Popover>
      )}
      {sameDayOnly && (
        <>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
            Partner-source jobs (AA / First Rescue) are same-day only.
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {QUICK_OPTIONS.map((q) => (
              <button
                key={q.label}
                type="button"
                onClick={() => (q.mins === 0 ? onChange(toLocalInput(new Date())) : bump(q.mins))}
                className="text-xs font-semibold px-2.5 py-1 rounded-md border border-slate-200 bg-slate-50 text-slate-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-800 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-blue-900/40 dark:hover:border-blue-700 dark:hover:text-blue-200 transition-colors"
                data-testid={`assign-quick-${q.label.toLowerCase().replace('+', 'plus')}`}
              >
                {q.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// One row inside the truck-selector dropdown. Shows status dot + name + driver
// + live ETA (or "no GPS") + queue position chip.
// ─────────────────────────────────────────────────────────────────────────────
export function TruckOption({ truck, eta, queueSize, isClosest }) {
  const c = TRUCK_STATUS_COLORS[truck.status];
  const isBusy = truck.status === "busy";
  // "Will queue as #N" = position in this truck's queue if assigned now.
  // queueSize already excludes completed/cancelled, so adding 1 gives the next slot.
  const queueSlot = isBusy ? queueSize + 1 : null;
  return (
    <SelectItem value={truck.id} data-testid={`assign-truck-opt-${truck.id}`}>
      <div className="flex items-center gap-2 w-full">
        <span className={cn("h-2 w-2 rounded-full shrink-0", c?.dot)} />
        <span className="font-semibold">{truck.name}</span>
        {truck.driver && <span className="text-slate-500">· {truck.driver}</span>}
        {eta ? (
          <span
            className={cn(
              "ml-auto flex items-center gap-1 text-[11px] font-bold px-1.5 py-0.5 rounded",
              isClosest ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"
            )}
            data-testid={`assign-truck-eta-${truck.id}`}
          >
            <Route className="h-3 w-3" />
            {formatDuration(eta.duration_s)} · {formatDistance(eta.distance_m)}
          </span>
        ) : (
          <span className="ml-auto text-[11px] text-slate-400">no GPS</span>
        )}
        {isBusy ? (
          <span
            className="text-[10px] font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded whitespace-nowrap"
            data-testid={`assign-truck-queueslot-${truck.id}`}
          >
            Queue #{queueSlot}
          </span>
        ) : queueSize > 0 ? (
          <span className="text-[10px] font-bold bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
            {queueSize} queued
          </span>
        ) : null}
      </div>
    </SelectItem>
  );
}
